import { useQueryClient } from '@tanstack/react-query';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Typography from 'antd/es/typography';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { WhoamiResponseSchema } from '@mai/types';

import CenterSpin from '@components/CenterSpin';
import UnauthCard from '@components/UnauthCard';
import { apiClient } from '@queries/axios';
import { useSessionUserId } from '@utils/hooks';
import { track } from '@utils/mixpanel';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  height: 100px;
`;

const MagicLinkPage = () => {
  const { updateUserId } = useSessionUserId();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [error, setError] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError(true);
        return;
      }

      try {
        await apiClient.post('/authentication/email/verify', {
          token,
        });

        const whoamiResponse = await apiClient.get('/authentication/whoami');
        const whoamiData = WhoamiResponseSchema.parse(whoamiResponse.data);
        updateUserId(whoamiData.userId);

        void message.success('Login successful');
        track('LOGGED_IN', {
          loginMethod: 'magic-link',
        });
        await queryClient.clear();
        navigate('/');
      } catch (error) {
        setError(true);
      }
    };

    void verifyToken();
  }, [token, navigate, message, queryClient, updateUserId]);

  if (error) {
    return (
      <Container>
        <UnauthCard>
          <ContentContainer>
            <Typography.Title level={3}>Invalid Link</Typography.Title>
            <Typography.Text>
              This login link is invalid or has expired. Please return to the
              signin page to continue.
            </Typography.Text>
            <Link to="/auth/login">
              <Button type="primary">Back to Sign In</Button>
            </Link>
          </ContentContainer>
        </UnauthCard>
      </Container>
    );
  }

  return (
    <Container>
      <UnauthCard>
        <LoadingContainer>
          <CenterSpin />
        </LoadingContainer>
      </UnauthCard>
    </Container>
  );
};

export default MagicLinkPage;
